import { FC, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { navItems } from './constants';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import type { TherapistDashboardSection } from '../../../types/therapist-dashboard.types';

interface SidebarProps {
  isOpen: boolean;
  activeSection: TherapistDashboardSection;
  onToggle: () => void;
  onSectionChange: (section: TherapistDashboardSection) => void;
  onLogout: () => void;
}

export const Sidebar: FC<SidebarProps> = ({
  isOpen,
  activeSection,
  onToggle,
  onSectionChange,
  onLogout,
}) => {
  const sidebarRef = useRef<HTMLElement>(null);

  // Trap focus within sidebar when open on mobile
  useFocusTrap(sidebarRef, { enabled: isOpen });

  // Close sidebar on Escape key
  useEscapeKey(onToggle, isOpen);

  return (
    <>
      {/* Mobile Sidebar Toggle */}
      <button
        onClick={onToggle}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-md hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-controls="therapist-sidebar"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-gray-700 dark:text-slate-300" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700 dark:text-slate-300" />
        )}
      </button>

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        id="therapist-sidebar"
        role="navigation"
        aria-label="Therapist dashboard navigation"
        className={cn(
          'fixed top-0 left-0 h-full w-64 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700',
          'transform transition-transform duration-300 ease-in-out z-40 shadow-lg',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-200 dark:border-slate-700">
          <Link
            to="/"
            className="flex items-center space-x-2"
            onClick={onToggle}
          >
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div>
              <span className="text-lg font-bold text-gray-900 dark:text-slate-100 block">assaTherapy</span>
              <span className="text-xs text-gray-500 dark:text-slate-400">Therapist Portal</span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => onSectionChange(item.id)}
                    className={cn(
                      'w-full flex items-center space-x-3 px-4 py-3 rounded-lg',
                      'text-sm font-medium transition-all duration-200',
                      'hover:bg-gray-50 dark:hover:bg-slate-700 active:scale-95',
                      isActive ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800' : 'text-gray-700 dark:text-slate-300'
                    )}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all active:scale-95"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}
    </>
  );
};
